import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components/macro';
import delve from 'dlv';
import useOffset from 'hooks/useOffset';
import { PromiseState } from 'react-refetch';
import isEqual from 'lodash.isequal';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import Pagination from 'components/data-visualization/Table/Pagination';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Table from 'components/data-visualization/Table/Table';
import TableBody from 'components/data-visualization/Table/Body';
import TableRowLoader from 'components/data-containers/BrowseEntities/TableRow/TableRow.loader';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import { useHistory } from 'react-router';
import EditorTableRow from 'components/customlists/BrowseCustomListTable/EditorTableRow';
import TableHead from 'components/customlists/BrowseCustomListTable/TableHead';
import { IBrowseCustomList, IBrowseCustomListEntityInfo } from './shared';
import { DndProvider } from 'react-dnd';
import TableRowPreview from 'components/customlists/BrowseCustomListTable/TableRowPreview';
import useReorderDragDropBackend from 'hooks/useReorderDragDropBackend';
import { TEntityInfo } from 'components/customlists/AddToList/shared';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

const CustomListTable = styled(Table)`
  & th:nth-child(2) div {
    justify-content: flex-start;
  }
`;

const hideVisually = new Set<string>(['reorder', 'delete']);

interface IEditListProps {
  allowedFilters: TopicFilterNames[];
  list: PromiseState<IBrowseCustomList>;
  allLists: PromiseState<IBrowseCustomList>;
  onSave: (list: IBrowseCustomList) => any;
  isLargeScreen: boolean;
  isPending: boolean;
  setLimit: any;
  setOffsetState: any;
  isSameFilters: boolean;
  totalLimit: number;
}

/*
 * Variation of the browse table shows all the entities in a custom list
 * and allows to delete and re-order them.
 * A custom list entity may be deleted by clicking in its delete button in which
 * case the row is rendered differently without a delete and drag (re-order) button, but with
 * an undelete button. A delete is only final after the update button has been clicked
 * an the entire customlist has been saved to the server.
 * After a cancel or update the user is routed back to the view list.
 * A loading state (TableRowLoader) is shown while the data is being retrieved.
 * Table headers are only rendered in a desktop view.
 */
const EditList: React.FC<IEditListProps> = (props) => {
  const {
    allowedFilters,
    list = PromiseState.create<IBrowseCustomList>(),
    allLists = PromiseState.create<IBrowseCustomList>(),
    onSave,
    isLargeScreen,
    isPending,
    setLimit,
    setOffsetState,
    isSameFilters,
    totalLimit,
  } = props;
  const increment = 100;
  const { offsetState, setOffset, setEntities, setInvalidate, resetOffset } =
    useOffset({
      limit: totalLimit,
      increment,
      entitiesKey: 'entities',
    });

  const { limit, offset, value } = offsetState;
  const listData = {
    ...list.value,
    entities: list.value?.entities?.filter((entity) => entity !== undefined),
  };
  const entityCount = delve(listData, 'totalEntityCount');
  const entities: IBrowseCustomListEntityInfo[] = delve(value, 'entities');

  const { pending, rejected, reason } = list;
  const [isButtonDisabled, setDisabledButton] = useState(false);
  const [listEntities, setListEntities] = useState<
    IBrowseCustomListEntityInfo[]
  >([]);
  const [disableDelete, setDisableDelete] = useState(listEntities?.length <= 1);
  const history = useHistory();

  useEffect(() => {
    setLimit(limit);
    setOffsetState(offset);
  }, [limit, offset, setLimit, setOffsetState]);

  useEffect(() => {
    if (listEntities) {
      setDisableDelete(listEntities?.length <= 1);
    }
  }, [setDisableDelete, listEntities]);

  useEffect(() => {
    const isEqualEntities = isEqual(entities, list?.value?.entities);
    if ((list?.value?.entities && !isEqualEntities) || !isSameFilters) {
      const data = list.value?.entities?.filter(
        (entity) => entity !== undefined
      );
      setEntities({
        entities: data,
        totalEntityCount: list.value.totalEntityCount,
      });
      const prevEntities = offset > 0 ? listEntities : [];
      setListEntities([...prevEntities, ...data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, setEntities, entities, isSameFilters]);

  useEffect(() => {
    // `setInvalidate` flushes the entities when the next batch of entities arrive.
    // Happens at the next `setEntities` so we don't see a skeleton loader in between.
    setInvalidate();
  }, [setInvalidate, isSameFilters]);

  useEffect(
    () => resetOffset(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const shouldDisableDelete = (
    listEntities: IBrowseCustomListEntityInfo[]
  ): boolean => {
    return (
      listEntities.filter(
        (entity: IBrowseCustomListEntityInfo): boolean => !entity?.deleted
      ).length === 1
    );
  };

  const onDelete = (entityToDelete: IBrowseCustomListEntityInfo): void => {
    entityToDelete.deleted = true;
    setDisableDelete(shouldDisableDelete(listEntities));
  };

  const onUndelete = (entityToUndelete: IBrowseCustomListEntityInfo): void => {
    delete entityToUndelete.deleted;
    setDisableDelete(shouldDisableDelete(listEntities));
  };

  const tableHeaders = [
    { label: `Delete`, value: 'delete' },
    { label: `Name`, value: 'displayName' },
    { label: `Reorder`, value: 'reorder' },
  ];

  const onClick = (): void => {
    if (isButtonDisabled) return;
    setDisabledButton(true);
    const customEntityentitiesInput = listEntities
      .filter((l: IBrowseCustomListEntityInfo) => !l.deleted)
      .map<TEntityInfo>(
        (listInfo: IBrowseCustomListEntityInfo): TEntityInfo => {
          return {
            id: listInfo.entity?.id,
            type: listInfo.entity?.type,
          };
        }
      );
    const newListData: any = {
      ...listData,
    };
    const additionalData =
      allLists?.value?.entities?.length !== listEntities.length
        ? allLists?.value?.entities
            ?.slice(listEntities.length)
            .map((listInfo) => ({
              id: listInfo.entity?.id,
              type: listInfo.entity?.type,
            }))
        : [];
    newListData.entities = [...customEntityentitiesInput, ...additionalData];
    onSave(newListData);
    logToAmplitude('User has edited a list');
  };

  const [DragDropBackend, backendOptions, isTouchDevice] =
    useReorderDragDropBackend();

  const changeEntityOrder = useCallback(
    (oldIndex: number, newIndex: number) => {
      const temp = [...listEntities];
      const entity = temp[oldIndex];

      /** Move the item from its old position to the new position by changing the order of the array. */
      temp.splice(oldIndex, 1);
      temp.splice(newIndex, 0, entity);

      setListEntities(temp);
    },
    [listEntities, setListEntities]
  );
  const onCancel = (): void => history.push(`/list/${listData?.id}`);

  return (
    <DndProvider backend={DragDropBackend} options={backendOptions}>
      <GenericComponent
        title="Edit list"
        loading={pending}
        error={rejected}
        allowedFilters={allowedFilters}
        data-pw="edit-custom-list"
        showToolbarDropdown={false}
      >
        <Box
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          alignContent="stretch"
        >
          <Box alignSelf="auto" marginLeft="auto">
            <Button
              variant={isButtonDisabled || isPending ? 'disabled' : 'secondary'}
              disabled={isButtonDisabled || isPending}
              py={'.75rem'}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant={isButtonDisabled || isPending ? 'disabled' : 'primary'}
              disabled={isButtonDisabled || isPending}
              marginLeft={'.5em'}
              py={'.75rem'}
              onClick={onClick}
              data-gtag="custom-list-update-list"
              data-pw="custom-list-update-list"
            >
              Update list
            </Button>
          </Box>
        </Box>
        <ErrorBoundary error={reason} errorInfo={reason?.cause}>
          <AnimatedLoadingWrapper loading={pending}>
            <CustomListTable>
              {isLargeScreen && (
                <TableHead headers={tableHeaders} hideVisually={hideVisually} />
              )}
              <TableBody data-gtag="edit-custom-entity-list">
                {listEntities && listEntities.length > 0 ? (
                  listEntities.map(
                    (
                      listInfo: IBrowseCustomListEntityInfo,
                      index: number
                    ): any =>
                      listInfo?.entity && (
                        <EditorTableRow
                          index={index}
                          key={listInfo.entity.id}
                          entity={listInfo.entity}
                          entityData={listInfo.data}
                          onDelete={(): void => onDelete(listInfo)}
                          onUndelete={(): void => onUndelete(listInfo)}
                          disableDelete={disableDelete}
                          changeEntityOrder={changeEntityOrder}
                        />
                      )
                  )
                ) : (
                  <TableRowLoader />
                )}
                {isTouchDevice && <TableRowPreview key="__preview" />}
              </TableBody>
            </CustomListTable>
            {listEntities && listEntities.length > 0 && (
              <Pagination
                current={listEntities.length}
                total={entityCount}
                offset={increment}
                hasMoreResults={listEntities.length < entityCount}
                onClick={setOffset}
              />
            )}
          </AnimatedLoadingWrapper>
        </ErrorBoundary>
      </GenericComponent>
    </DndProvider>
  );
};

export default EditList;
